import {useRef, useCallback, useEffect} from 'react'
import Logger from '../services/Logger'

/**
 * Generic handler for differentiating between single and double clicks.
 *
 * @param onClick        Fires when the delay elapses without a second click
 * @param onDoubleClick  Fires immediately on the second click
 * @param delay          Time window in ms (default = 250)
 * @returns              A handler function that processes click events
 */
export const useClickOrDoubleClick = <
  T extends React.SyntheticEvent = React.MouseEvent,
>(
  onClick: (e: T) => void,
  onDoubleClick: (e: T) => void,
  delay = 250,
) => {
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  // Cleanup timer on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = undefined
      }
    }
  }, [])

  return useCallback(
    (e: T) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = undefined // second click → treat as dblclick
        try {
          onDoubleClick(e)
        } catch (error) {
          Logger.LogError(
            error instanceof Error
              ? error
              : new Error('Error in onDoubleClick handler'),
          )
        }
      } else {
        timerRef.current = setTimeout(() => {
          timerRef.current = undefined // single click after timeout
          try {
            onClick(e)
          } catch (error) {
            Logger.LogError(
              error instanceof Error
                ? error
                : new Error('Error in onClick handler'),
            )
          }
        }, delay)
      }
    },
    [onClick, onDoubleClick, delay],
  )
}
